import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";

import type { Incident } from "../view-model";

export interface IncidentsStore {
  // State
  activeIncidents: Incident[];
  allIncidents: Incident[];
  isLoading: boolean;
  error: Error | null;

  // Actions
  setActiveIncidents: (incidents: Incident[]) => void;
  setAllIncidents: (incidents: Incident[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: Error | null) => void;
  patchIncident: (
    incidentId: string,
    deviceId: string,
    patch: Partial<Pick<Incident, "acknowledged" | "confirmed">>
  ) => void;

  // Computed selectors
  getActiveIncidents: () => Incident[];
  getIncidentsBySeverity: (severity: "critical" | "warning") => Incident[];
}

export const useIncidentsStore = create<IncidentsStore>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      // Initial state
      activeIncidents: [],
      allIncidents: [],
      isLoading: false,
      error: null,

      // Actions
      setActiveIncidents: (incidents: Incident[]) => {
        set({ activeIncidents: incidents }, false, "setActiveIncidents");
      },

      setAllIncidents: (incidents: Incident[]) => {
        set({ allIncidents: incidents }, false, "setAllIncidents");
      },

      setLoading: (isLoading: boolean) => {
        set({ isLoading }, false, "setLoading");
      },

      setError: (error: Error | null) => {
        set({ error }, false, "setError");
      },

      patchIncident: (
        incidentId: string,
        _deviceId: string,
        patch: Partial<Pick<Incident, "acknowledged" | "confirmed">>
      ) => {
        const { activeIncidents, allIncidents } = get();

        const updateIncidents = (incidents: Incident[]) =>
          incidents.map((incident) =>
            incident.id === incidentId ? { ...incident, ...patch } : incident
          );

        set(
          {
            activeIncidents: updateIncidents(activeIncidents),
            allIncidents: updateIncidents(allIncidents),
          },
          false,
          "patchIncident"
        );
      },

      // Computed selectors
      getActiveIncidents: () => {
        const { activeIncidents } = get();
        return activeIncidents;
      },

      getIncidentsBySeverity: (severity: "critical" | "warning") => {
        const { allIncidents } = get();
        return allIncidents.filter(
          (incident) => incident.severity === severity
        );
      },
    })),
    {
      name: "incidents-store",
    }
  )
);
